import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getContractAssetsBundle, startMaintenanceEvent } from "@/lib/wartungen";

interface LocationAsset {
  asset_id?: number;
  external_id?: string | null;
  name?: string | null;
  status?: string | null;
  location_label?: string | null;
  location_path?: string[];
  hero_address_id?: number | null;
  [k: string]: unknown;
}

interface TreeNode {
  label: string;
  trail: string[];
  children: Map<string, TreeNode>;
}

function num(params: URLSearchParams, key: string): number | null {
  const n = Number(params.get(key));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isPrefixPath(prefix: string[], full: string[]): boolean {
  if (!prefix.length) return true;
  if (prefix.length > full.length) return false;
  return prefix.every((seg, i) => String(full[i] || "") === String(seg || ""));
}

function buildTree(paths: string[][]): TreeNode {
  const root: TreeNode = { label: "Basisort", trail: [], children: new Map() };
  for (const path of paths) {
    let node = root;
    const trail: string[] = [];
    for (const segment of path) {
      trail.push(segment);
      if (!node.children.has(segment)) {
        node.children.set(segment, { label: segment, trail: [...trail], children: new Map() });
      }
      node = node.children.get(segment)!;
    }
  }
  return root;
}

export function StandortObjektAuswahlPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const contractId = num(params, "contract_id");
  const projectId = num(params, "project_id");
  const typeKey = (params.get("type_key") || params.get("maintenance_type_key") || params.get("type") || "").trim();
  const dateMode = (params.get("date_mode") || "day").trim();
  const dateValue = (params.get("date_value") || "").trim();
  const baseLocationName = (params.get("base_location_name") || "").trim();
  const heroAddressId = num(params, "hero_address_id");
  const subLocationId = num(params, "sub_location_id");

  const valid = contractId != null && projectId != null && Boolean(typeKey);

  const [assets, setAssets] = useState<LocationAsset[]>([]);
  const [baseLocation, setBaseLocation] = useState(baseLocationName);
  const [locationHint, setLocationHint] = useState("");
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [textFilter, setTextFilter] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ tone: "info" | "error" | "warning"; message: string } | null>(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    if (!valid) {
      setLoading(false);
      setFeedback({ tone: "error", message: "Fehlende Parameter: contract_id, project_id oder type_key." });
      return;
    }
    setLoading(true);
    try {
      const bundle = await getContractAssetsBundle(projectId!, typeKey);
      const contract = (bundle.contracts || []).find((c) => Number(c.contract_id) === contractId) || null;
      if (!contract) throw new Error("Vertrag nicht im Projekt verfügbar");
      const contractAssets = (Array.isArray(contract.assets) ? contract.assets : []) as LocationAsset[];
      const filtered = heroAddressId
        ? contractAssets.filter((a) => Number(a.hero_address_id) === heroAddressId)
        : contractAssets;
      setAssets(filtered);
      if (filtered.length === 0) {
        setFeedback({ tone: "warning", message: "Für diesen Vertrag sind keine Objekte hinterlegt." });
      } else {
        setFeedback(null);
      }

      const locations = (Array.isArray(contract.locations) ? contract.locations : []) as {
        hero_address_id?: number;
        location_name?: string;
        building_name?: string;
      }[];
      const selectedLoc = locations.find((l) => Number(l.hero_address_id) === heroAddressId);
      const label = String(selectedLoc?.location_name || selectedLoc?.building_name || baseLocationName || "").trim();
      setBaseLocation(
        label && heroAddressId
          ? `${label} (Hero-Adresse ${heroAddressId})`
          : label || (heroAddressId ? `Hero-Adresse ${heroAddressId}` : `Projekt ${projectId} / Vertrag ${contractId}`),
      );
      const locationCount = Number(contract.location_count || locations.length || 0);
      if (heroAddressId) {
        setLocationHint(
          locationCount > 1
            ? "Sie öffnen die Vertragswartung bereits für den ausgewählten Vertrags-Ort; angezeigt werden nur passende Assets."
            : "Die Asset-Auswahl ist auf den Vertrags-Ort eingeschränkt.",
        );
      } else if (locationCount > 1) {
        setLocationHint("Mehrfach-Orte-Vertrag ohne expliziten Ortsfilter: Assets bleiben nach Ort/Unterort gruppiert.");
      } else {
        setLocationHint("");
      }
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Objektdaten konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  }, [valid, projectId, typeKey, contractId, heroAddressId, baseLocationName]);

  useEffect(() => {
    void load();
  }, [load]);

  const tree = useMemo(
    () => buildTree(assets.map((a) => (Array.isArray(a.location_path) ? a.location_path : []))),
    [assets],
  );

  const filteredAssets = useMemo(() => {
    const tf = textFilter.trim().toLowerCase();
    return assets.filter((a) => {
      if (!isPrefixPath(selectedPath, Array.isArray(a.location_path) ? a.location_path : [])) return false;
      if (!tf) return true;
      return `${a.external_id || ""} ${a.name || ""}`.toLowerCase().includes(tf);
    });
  }, [assets, selectedPath, textFilter]);

  async function startMaintenance() {
    if (!valid || !selectedAssetId) {
      setFeedback({ tone: "warning", message: "Bitte zuerst ein Objekt auswählen." });
      return;
    }
    setStarting(true);
    setFeedback(null);
    try {
      const result = await startMaintenanceEvent(typeKey, {
        asset_id: selectedAssetId,
        project_id: projectId,
        contract_id: contractId,
        hero_address_id: heroAddressId,
        sub_location_id: subLocationId,
        date_mode: dateMode,
        date_value: dateValue,
      });
      const eventId = Number(result?.maintenance_event_id);
      if (!Number.isFinite(eventId) || eventId <= 0) throw new Error("maintenance_event_id fehlt");
      const usp = new URLSearchParams();
      usp.set("type_key", typeKey);
      usp.set("asset_id", String(selectedAssetId));
      usp.set("project_id", String(projectId));
      usp.set("contract_id", String(contractId));
      usp.set("maintenance_event_id", String(eventId));
      if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
      usp.set("date_mode", dateMode || "day");
      usp.set("date_value", dateValue || "");
      navigate(`/wartungen/form?${usp.toString()}`);
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Wartung konnte nicht gestartet werden.",
      });
      setStarting(false);
    }
  }

  const backTo = (() => {
    const usp = new URLSearchParams();
    if (typeKey) usp.set("type_key", typeKey);
    if (contractId) usp.set("contract_id", String(contractId));
    if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
    return `/wartungen/projekt-start?${usp.toString()}`;
  })();

  return (
    <AppShell
      title={`Standort & Objekt auswählen${typeKey ? ` – ${typeKey}` : ""}`}
      subtitle={
        valid
          ? `Kategorie: ${typeKey} · Vertrag: ${contractId}${heroAddressId ? ` · Ort: ${heroAddressId}` : ""}`
          : undefined
      }
      back={{ to: backTo, label: "zurück zum Projektstart" }}
    >
      {feedback ? (
        <Alert variant={feedback.tone === "error" ? "error" : feedback.tone === "warning" ? "warning" : "info"} className="mb-4">
          {feedback.message}
        </Alert>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Objektdaten …
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
          <aside className="rounded-ios border border-separator bg-card p-4 shadow-ios">
            <div className="mb-3">
              <span className="eh-label text-xs">Basisort</span>
              <p className="mt-1 text-sm text-label">{baseLocation || "–"}</p>
              {locationHint ? <p className="mt-1 text-xs text-label-secondary">{locationHint}</p> : null}
            </div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">Unterorte</h3>
            <div className="space-y-1">
              <TreeButton label="Basisort" active={selectedPath.length === 0} onClick={() => setSelectedPath([])} />
              <TreeBranch node={tree} selectedPath={selectedPath} onSelect={setSelectedPath} depth={0} />
            </div>
          </aside>

          <div>
            <input
              className="eh-input mb-3 w-full max-w-md"
              placeholder="Objekte filtern …"
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
            />
            <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-separator text-left text-xs uppercase tracking-wide text-label-tertiary">
                    <th className="px-3 py-2"></th>
                    <th className="px-3 py-2 font-medium">External-ID</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Ort</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssets.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-label-secondary">
                        Keine Objekte für diesen Unterort gefunden.
                      </td>
                    </tr>
                  ) : (
                    filteredAssets.map((item) => {
                      const id = Number(item.asset_id);
                      return (
                        <tr
                          key={id}
                          className={clsx(
                            "cursor-pointer border-b border-separator/60 hover:bg-fill-tertiary",
                            selectedAssetId === id && "bg-accent-soft",
                          )}
                          onClick={() => setSelectedAssetId(id)}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="radio"
                              name="selected-location-asset"
                              checked={selectedAssetId === id}
                              onChange={() => setSelectedAssetId(id)}
                            />
                          </td>
                          <td className="px-3 py-2">{item.external_id || "–"}</td>
                          <td className="px-3 py-2">{item.name || "–"}</td>
                          <td className="px-3 py-2">{item.location_label || "Basisort"}</td>
                          <td className="px-3 py-2">{item.status || "–"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <Button type="button" disabled={!selectedAssetId || starting} onClick={startMaintenance}>
                {starting ? "Starte …" : "Wartung starten"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function TreeButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "block w-full rounded-lg px-2 py-1 text-left text-sm transition",
        active ? "bg-accent text-label-on-accent" : "text-label hover:bg-fill-tertiary",
      )}
    >
      {label}
    </button>
  );
}

function TreeBranch({
  node,
  selectedPath,
  onSelect,
  depth,
}: {
  node: TreeNode;
  selectedPath: string[];
  onSelect: (path: string[]) => void;
  depth: number;
}) {
  const children = Array.from(node.children.values()).sort((a, b) => a.label.localeCompare(b.label, "de"));
  if (!children.length) return null;
  return (
    <div style={{ paddingLeft: depth > 0 ? 12 : 0 }} className="space-y-1">
      {children.map((child) => {
        const active = JSON.stringify(child.trail) === JSON.stringify(selectedPath);
        return (
          <div key={child.trail.join("/")}>
            <TreeButton label={child.label} active={active} onClick={() => onSelect(child.trail)} />
            <TreeBranch node={child} selectedPath={selectedPath} onSelect={onSelect} depth={depth + 1} />
          </div>
        );
      })}
    </div>
  );
}
